import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import {
  FacilityInformation,
  FacilityProcessDetails,
  FacilityFarmCapacityInformation,
} from "../../../sc-models/facility-product-dto";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { FarmCategoryUtils } from "../../../constants/farm-categories";
import {
  buildFacilityProcessValidationSchema,
  validateFacilityProcessRow,
} from "../../utils/scValidation";
import { useScStore } from "../../../../../../store/scStandard.store";

interface RawMaterialsInfoProps {
  facilityData: FacilityInformation;
  facilityProcessInfo: FacilityProcessDetails[]; // Expecting an array
  facilityFarmCapacityInfo: FacilityFarmCapacityInformation[]; // Added for validation
  setFacilityProcessInfo: React.Dispatch<
    React.SetStateAction<FacilityProcessDetails[]>
  >;
  setSubsequentFacilityFlag: React.Dispatch<React.SetStateAction<boolean>>;
  setScSubsequentFacilityButtonFlag: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  scNoOfFarmsFlag: boolean;
  setScNoOfFarmsFlag: React.Dispatch<React.SetStateAction<boolean>>;
  isFarmSite: boolean;
  setIsFarmSite: React.Dispatch<React.SetStateAction<boolean>>;
  subsequentFacilityFlag: boolean;
  scSubsequentFacilityButtonFlag: boolean;
  /** SC-level standards from useScStore — used to intersect with facility standards for validation */
  scStandards: string[];
  isSaving?: boolean;
  isLoading?: boolean;
}

const FacilityProcessInfo: React.FC<RawMaterialsInfoProps> = ({
  facilityData,
  facilityProcessInfo,
  facilityFarmCapacityInfo,
  setFacilityProcessInfo,
  setSubsequentFacilityFlag,
  setScSubsequentFacilityButtonFlag,
  scNoOfFarmsFlag,
  setScNoOfFarmsFlag,
  subsequentFacilityFlag,
  scSubsequentFacilityButtonFlag,
  isFarmSite,
  setIsFarmSite,
  scStandards,
  isSaving,
  isLoading,
}) => {
  const { updateField } = useFormActions("sc-facilitydetails");
  const { isFarmingAreaOnly } = useScStore();
  const [data, setData] =
    useState<FacilityProcessDetails[]>(facilityProcessInfo); // Initialize with prop data
  const [tableData, setTableData] =
    useState<FacilityProcessDetails[]>(facilityProcessInfo); // Initialize with prop data
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [facilityProcessCategoryCode, setFacilityProcessCategoryCode] =
    useState<any[]>([]);
  const {
    isRestrictPopUpSave,
    setRestrictPopUpSave,
    setFacilityProcessCategoryCodes,
  } = useTransactionCertificateStore();
  const { notify } = useNotification();

  // Derive the active standards for this facility:
  // For MF: use SC-level standards (scStandards prop). For others: intersect facility standards with SC standards.
  // This prevents validating against stale standards removed from the SC.
  const activeStandards: string[] = (() => {
    const facilityStds: string[] = Array.isArray(facilityData?.facilityStandard)
      ? (facilityData.facilityStandard as string[])
      : ([] as string[]).concat(facilityData?.facilityStandard ?? []).filter(Boolean);
    if (facilityData?.facilityType === "MF") return scStandards;
    const intersected = facilityStds.filter((s) => scStandards.includes(s));
    return intersected.length > 0 ? intersected : scStandards;
  })();

  // If scNoOfFarmsFlag is false (not a farm SC), completely hide PR0011, PR0036, PR0037, PR0038 from the dropdown options.
  // If it is a farm SC and involves Farming (area) only, restrict selection of PR0011, PR0036, PR0038.
  // Additionally, hide PR0037 when RDS is NOT in the facility's active standards — PR0037 is only applicable for RDS.
  const filteredFacilityProcessCategoryCode: any[] = facilityProcessCategoryCode.filter((option: any) => {
    if (!scNoOfFarmsFlag && ["PR0011", "PR0036", "PR0037", "PR0038"].includes(option.value)) {
      return false;
    }
    if (scNoOfFarmsFlag && isFarmingAreaOnly && ["PR0011", "PR0036", "PR0038"].includes(option.value)) {
      return false;
    }
    // PR0037 is only valid for RDS — hide it entirely when RDS is not in the active standards
    if (option.value === "PR0037" && !activeStandards.includes("RDS")) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    setRestrictPopUpSave(false);
    return () => {
      setRestrictPopUpSave(false);
    };
  }, []);
  const validationSchema = buildFacilityProcessValidationSchema();

  useEffect(() => {
    const getRawMaterialCodes = async () => {
      try {
        const result: any =
          await dtrackItServiceV4.getFacilityProcessCategoryCodes();
        const rawMaterials = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setFacilityProcessCategoryCode(rawMaterials);
        setFacilityProcessCategoryCodes(rawMaterials);

        // Back-fill descriptions for existing rows that were loaded from the
        // backend without a description (e.g. rows saved before this field existed).
        const optionMap = new Map<string, string>(rawMaterials.map((o: any) => [o.value, o.label]));
        setData((prevData) =>
          prevData.map((row) => {
            if (row.facilityProcessCategoryCode && !row.facilityProcessCategoryCodeDescription) {
              const fullLabel = optionMap.get(row.facilityProcessCategoryCode) ?? '';
              const description = fullLabel.replace(/\s*\([^)]+\)\s*$/, '').trim();
              return { ...row, facilityProcessCategoryCodeDescription: description };
            }
            return row;
          })
        );
      } catch (err) {
        console.log(`Error in Fetching Organic Farm Standard Code ${err}`);
      }
    };

    getRawMaterialCodes();
  }, []);

  useEffect(() => {
    updateField("facilityProcessDetails", data);
  }, [data]);

  // Initialize isFarmSite when component loads with existing data
  useEffect(() => {
    if (facilityProcessInfo && facilityProcessInfo.length > 0) {
      const hasFarmCategory = facilityProcessInfo.some((process) =>
        FarmCategoryUtils.isFarmCode(process?.facilityProcessCategoryCode)
      );
      setIsFarmSite(hasFarmCategory);
    }
  }, [facilityProcessInfo, setIsFarmSite]);

  const formik = useFormik<FacilityProcessDetails>({
    initialValues: {
      facilityProcessCategoryCode: "",
      facilityProcessCategoryCodeDescription: "",
      facilityProcessCategoryUserSpecificTerm: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const stripCode = (label: string) => label.replace(/\s*\([^)]+\)\s*$/, '').trim();

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    if (columnId === 'facilityProcessCategoryCode') {
      formik.setFieldValue(columnId, value);
      const selectedOption = filteredFacilityProcessCategoryCode.find((option: any) => option.value === value);
      if (selectedOption) {
        const description = stripCode(selectedOption.label);
        formik.setFieldValue('facilityProcessCategoryCodeDescription', description);
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex
              ? { ...row, [columnId]: value, facilityProcessCategoryCodeDescription: description }
              : row
          )
        );
      } else {
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, [columnId]: value } : row
          )
        );
      }
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
  };

  const handleAddNewRow = () => {
    const newRow: any = {
      facilityProcessCategoryCode: "",
      facilityProcessCategoryCodeDescription: "",
      facilityProcessCategoryUserSpecificTerm: "",
    };
    setRestrictPopUpSave(true);
    const newData = [...data, newRow];
    setData(newData);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
    formik.setErrors({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;

    // // Determine if the row being edited is new by comparing `editingRowIndex` with the data length
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }

    setData(tableData);

    // Reset editing state and form
    setRestrictPopUpSave(false);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null || editingRowIndex === -1) {
      notify('No row selected for editing', 'error');
      return;
    }

    const currentRowData = data[editingRowIndex];
    const { fieldErrors, generalError } = validateFacilityProcessRow(
      currentRowData,
      {
        rows: data,
        editingRowIndex,
        scNoOfFarmsFlag,
        activeStandards,
      }
    );

    if (Object.keys(fieldErrors).length > 0) {
      Object.values(fieldErrors).forEach((message) => {
        notify(message, 'error');
      });
      return;
    }

    if (generalError) {
      notify(generalError, 'error');
      return;
    }

    try {
      const updatedRow = currentRowData;

      // Build merged data and update isFarmSite (controls Farm Capacity stepper visibility)
      const newData = data?.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
      const hasFarmCategory = newData.some((value) =>
        FarmCategoryUtils.isFarmCode(value.facilityProcessCategoryCode)
      );
      setIsFarmSite(hasFarmCategory);

      setData(newData);
      setTableData(newData);
      setFacilityProcessInfo(newData);
      setRestrictPopUpSave(false);
      setEditingRowIndex(-1);
      formik.resetForm();

      notify('Facility process details saved successfully', 'success');
    } catch (error) {
      console.error('Error saving facility process details:', error);
      notify('Error saving facility process details', 'error');
    }
  };

  const columns: EditableColumnDef<FacilityProcessDetails>[] = [
    {
      accessorKey: "facilityProcessCategoryCode",
      header: "Facility Process Category Code",
      Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i>Process Category Code
        </span>
      ),
      size: 200,
      minSize: 180,
      maxSize: 260,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="facilityProcessCategoryCode"
          value={value}
          placeholder="Enter value"
          options={filteredFacilityProcessCategoryCode}
          enableSearch={true}
          showCodeOnlyWhenSelected={true}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ),
    },
    {
      accessorKey: "facilityProcessCategoryCodeDescription",
      header: "Description",
      Header: <span>Description</span>,
      size: 220,
      minSize: 160,
      maxSize: 320,
      grow: true,
    },
    {
      accessorKey: "facilityProcessCategoryUserSpecificTerm",
      header: "Facility Process Category User Specific Term",
      Header: (
        <span>
          {data[editingRowIndex]?.facilityProcessCategoryCode === "PR0034" && (
            <i style={{ color: "red" }}>* </i>
          )}
          User Specific Term
        </span>
      ),
      size: 240,
      minSize: 180,
      maxSize: 300,
      grow: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="facilityProcessCategoryUserSpecificTerm"
          required={data[editingRowIndex]?.facilityProcessCategoryCode === "PR0034"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={data[editingRowIndex]?.facilityProcessCategoryCode === "PR0034" ? "Required for PR0034" : "Enter value (optional)"}
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    
    // Ensure all required fields are set in formik including description
    const rowData = data[rowIndex];
    const formikValues = {
      facilityProcessCategoryCode: rowData.facilityProcessCategoryCode || "",
      facilityProcessCategoryCodeDescription: rowData.facilityProcessCategoryCodeDescription || "",
      facilityProcessCategoryUserSpecificTerm: rowData.facilityProcessCategoryUserSpecificTerm || "",
    };
    
    formik.setValues(formikValues);
    formik.setTouched({});
    formik.setErrors({});
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    if (!subsequentFacilityFlag && !scSubsequentFacilityButtonFlag) {
      setSubsequentFacilityFlag(true);
    }
    setData(updatedData);
    setTableData(updatedData);
    setFacilityProcessInfo(updatedData); // Update parent with updated data
    
    // Update isFarmSite after deletion
    const hasFarmCategory = updatedData.some((process) =>
      FarmCategoryUtils.isFarmCode(process?.facilityProcessCategoryCode)
    );
    setIsFarmSite(hasFarmCategory);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          border: "2px solid rgba(128, 128, 128, .2)",
          my: 4,
        }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton
            disabled={!formik.isValid || isRestrictPopUpSave || isSaving || isLoading}
            onClick={handleAddNewRow}
            sx={{ mb: 2 }}
          >
            Add Facility Process Information
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!isSaving && !isLoading}
          isSaving={isSaving}
        />
      </Box>
    </div>
  );
};

export default FacilityProcessInfo;
